import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import AuthContext from 'units/auth/AuthContext'
import FetchContext from 'context/fetch/FetchContext'
import useQueryLibrary from 'hooks/useQueryLibrary'
import { generateTimestampedId } from 'helpers/unique-id.helper'
import { AnnouncementExposures } from 'units/announcements/AnnouncementExposures'
import { AnnouncementPoller, DEFAULT_POLL_INTERVAL_SECONDS } from 'units/announcements/AnnouncementPoller'
import { ExposureTracker } from 'units/announcements/ExposureTracker'
import { ImpressionRetention } from 'units/announcements/ImpressionRetention'
import { PopupOwnership } from 'units/announcements/PopupOwnership'
import { PopupWorkingSet } from 'units/announcements/PopupWorkingSet'
import { SeenAnnouncements } from 'units/announcements/SeenAnnouncements'
import { shouldOpenAtSessionStart } from 'units/announcements/openingDecision'
import AnnouncementPopup from 'components/announcements/AnnouncementPopup'
import AnnouncementsContext from './AnnouncementsContext'
import useRenderTrace from 'units/debug/useRenderTrace'

const ANNOUNCEMENTS_CLAIMANT = 'announcements'
const RETENTION_REFRESH_MILLISECONDS = 30000

export default function AnnouncementsProvider({ children }) {
    const { authenticated, user } = useContext(AuthContext)
    const fetcher = useContext(FetchContext)
    const queryLibrary = useQueryLibrary()

    const [announcements, setAnnouncements] = useState([])
    const [pollIntervalSeconds, setPollIntervalSeconds] = useState(DEFAULT_POLL_INTERVAL_SECONDS)
    const [popup, setPopup] = useState(null)

    const announcementsRef = useRef(announcements)
    announcementsRef.current = announcements

    const seen = useMemo(() => new SeenAnnouncements(), [])

    const markSeen = useCallback((announcement) => {
        if (seen.remember(announcement)) {
            setAnnouncements((previous) => seen.applyTo(previous))
        }
    }, [seen])

    const poller = useMemo(() => new AnnouncementPoller({
        announcements: (payload) => setAnnouncements(seen.applyTo(payload ?? [])),
    }), [seen])

    const receiveAnnouncements = useCallback((response) => {
        poller.receive(response)
        setPollIntervalSeconds(poller.intervalSeconds)
    }, [poller])

    useQuery({
        queryKey: queryLibrary.announcementsForCurrentUserQuery(),
        queryFn: async () => {
            const response = await fetcher.fetchAnnouncementsForCurrentUser()
            receiveAnnouncements(response)

            return response
        },
        enabled: Boolean(authenticated),
        refetchInterval: pollIntervalSeconds * 1000,
        refetchOnWindowFocus: false,
    })

    const userId = user?.id ?? null

    const retention = useMemo(
        () => new ImpressionRetention({ tabId: generateTimestampedId() }),
        [],
    )

    const recordImpression = useMutation({
        mutationFn: (impression) => fetcher.authenticatedFetch({
            method: 'post',
            path: `/announcements/${impression.announcement}/impressions`,
            data: {
                data: {
                    placement: impression.placement,
                    trigger: impression.trigger,
                    startedAt: impression.startedAt,
                    dwellDuration: impression.dwellDuration,
                },
            },
            headers: {
                'Content-Type': 'application/json',
            },
        }),
    })

    const submitImpression = recordImpression.mutateAsync

    const recordConversion = useMutation({
        mutationFn: ({ announcement, source, outcome }) => fetcher.authenticatedFetch({
            method: 'post',
            path: `/announcements/${announcement.documentId}/conversions`,
            data: {
                data: {
                    source,
                    outcome,
                },
            },
            headers: {
                'Content-Type': 'application/json',
            },
        }),
    })

    const submitConversion = recordConversion.mutateAsync

    const resolveConversion = useMutation({
        mutationFn: ({ announcement, conversionId, outcome }) => fetcher.authenticatedFetch({
            method: 'put',
            path: `/announcements/${announcement.documentId}/conversions/${conversionId}`,
            data: { data: { outcome } },
            headers: {
                'Content-Type': 'application/json',
            },
        }),
    })

    const submitResolution = resolveConversion.mutateAsync

    const exposures = useMemo(() => {
        const bar = new ExposureTracker({
            placement: 'bar',
            userId,
            submit: submitImpression,
            retention,
        })
        const popup = new ExposureTracker({
            placement: 'popup',
            userId,
            submit: submitImpression,
            retention,
        })

        return new AnnouncementExposures({ bar, popup })
    }, [userId, submitImpression, retention])

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                exposures.pause()
            }
            else {
                exposures.resume()
            }
        }

        const onPageHide = () => exposures.pause()

        document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('pagehide', onPageHide)

        const refresh = setInterval(() => exposures.refresh(), RETENTION_REFRESH_MILLISECONDS)

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange)
            window.removeEventListener('pagehide', onPageHide)
            clearInterval(refresh)
        }
    }, [exposures])

    const popupOwnership = useMemo(() => new PopupOwnership(), [])

    const closePopup = useCallback(() => {
        popupOwnership.release(ANNOUNCEMENTS_CLAIMANT)
        setPopup(null)
        exposures.closePopup()
    }, [popupOwnership, exposures])

    const openPopup = useCallback((startAt = null, trigger = 'sessionStart') => {
        const working = announcementsRef.current
        if (working.length === 0) {
            return null
        }

        if (!popupOwnership.claim(ANNOUNCEMENTS_CLAIMANT, {
            onDisplaced: () => {
                setPopup(null)
                exposures.closePopup()
            },
        })) {
            return null
        }

        const workingSet = new PopupWorkingSet(working, startAt)
        setPopup(workingSet)
        exposures.openPopup(workingSet.current, trigger)
        markSeen(workingSet.current)

        return workingSet
    }, [popupOwnership, exposures, markSeen])

    const showBar = useCallback(
        (announcement, trigger) => exposures.showBar(announcement, trigger),
        [exposures],
    )

    const recoverRetainedExposures = useCallback(async () => {
        if (userId == null) {
            return
        }

        await Promise.all(retention.recoverFor(userId).map(async (impression) => {
            try {
                await submitImpression(impression)
                retention.discard(impression.id)
            }
            catch (error) {
                console.error('AnnouncementsProvider: could not record a retained impression:', error)
            }
        }))
    }, [retention, submitImpression, userId])

    const receiveSessionStart = useCallback((payload) => {
        const received = seen.applyTo(payload ?? [])
        setAnnouncements(received)
        announcementsRef.current = received

        recoverRetainedExposures()

        if (shouldOpenAtSessionStart(received)) {
            openPopup(null, 'sessionStart')
        }
    }, [openPopup, recoverRetainedExposures, seen])

    const value = useMemo(() => ({
        announcements,
        popup,
        pollIntervalSeconds,
        popupOwnership,
        openPopup,
        closePopup,
        showBar,
        receiveAnnouncements,
        receiveSessionStart,
    }), [
        announcements,
        popup,
        pollIntervalSeconds,
        popupOwnership,
        openPopup,
        closePopup,
        showBar,
        receiveAnnouncements,
        receiveSessionStart,
    ])

    useRenderTrace('AnnouncementsProvider', {
        authenticated,
        user,
        fetcher,
        announcements,
        pollIntervalSeconds,
        popup,
        exposures,
        retention,
        submitImpression,
        openPopup,
        receiveSessionStart,
        value,
    })

    return (
        <AnnouncementsContext.Provider value={value}>
            {children}
            <AnnouncementPopup
                workingSet={popup}
                onClose={closePopup}
                onNavigated={(announcement, trigger) => {
                    exposures.navigatePopup(announcement, trigger)
                    markSeen(announcement)
                }}
                onImageFailed={() => exposures.imageFailed()}
                onConverted={(announcement, request) => (
                    submitConversion({ announcement, ...request })
                )}
                onResolved={(announcement, conversionId, outcome) => (
                    submitResolution({ announcement, conversionId, outcome })
                )}
            />
        </AnnouncementsContext.Provider>
    )
}
